import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, GraduationCap, AlertTriangle, Mail, Send } from 'lucide-react';
import { apiFetch } from '../../services/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  A: { color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', label: 'A-Kunde (Prio)' },
  B: { color: 'bg-sky-500/20 text-sky-300 border-sky-500/40', label: 'B-Kunde (Standard)' },
  C: { color: 'bg-rose-500/20 text-rose-300 border-rose-500/40', label: 'C-Kunde (Kündigen)' },
};

/**
 * Companies-Tab mit A/B/C-Kundensegmentierung (Alex-Fischer-Modell).
 * Extrahiert aus AdminPage.jsx (Iter 27), erweitert um Tier-Dropdown
 * und Kündigungs-Marker.
 */
const CompaniesTab = ({ companies = [] }) => {
  const [tiers, setTiers] = useState(() => {
    const m = {};
    for (const c of companies) m[c._id] = c.customer_tier || null;
    return m;
  });
  const [termFlags, setTermFlags] = useState(() => {
    const m = {};
    for (const c of companies) m[c._id] = !!c.termination_pending;
    return m;
  });
  const [testingCron, setTestingCron] = useState(false);
  const [cronResult, setCronResult] = useState(null);

  const runCronTest = async () => {
    setTestingCron(true);
    setCronResult(null);
    try {
      const res = await apiFetch(`${API}/api/admin/cron/weekly-ampel/test`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      setCronResult(data.result);
      toast.success('Ampel-Cron ausgeführt', {
        description: `Sent: ${data.result?.sent || 0} · Skipped: ${data.result?.skipped || 0}`,
      });
    } catch (e) {
      toast.error(e.message || 'Cron fehlgeschlagen');
    } finally {
      setTestingCron(false);
    }
  };

  const inviteToBeta = async (companyId) => {
    try {
      const res = await apiFetch(`${API}/api/admin/companies/${companyId}/beta-invite-draft`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      // Mail-Client öffnen mit vorbereitetem Draft
      window.location.href = data.mailto;
      toast.success('Beta-Einladung vorbereitet', {
        description: `Draft an ${data.to} — Mail-Client öffnet sich.`,
      });
    } catch (e) {
      toast.error(e.message || 'Beta-Draft nicht möglich');
    }
  };

  const setTier = async (companyId, tier) => {
    setTiers((prev) => ({ ...prev, [companyId]: tier }));
    try {
      const res = await apiFetch(`${API}/api/admin/companies/${companyId}/tier`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier }),
      });
      if (!res.ok) throw new Error('Update fehlgeschlagen');
      toast.success(`Tier auf ${tier} gesetzt`);
    } catch (e) {
      toast.error(e.message || 'Fehler');
    }
  };

  const markForTermination = async (companyId) => {
    const reason = prompt('Grund für Kündigung (optional):', '');
    if (reason === null) return; // abgebrochen
    try {
      const res = await apiFetch(`${API}/api/admin/companies/${companyId}/mark-for-termination`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error('Fehler');
      setTermFlags((p) => ({ ...p, [companyId]: true }));
      toast.success('Firma zur Kündigung markiert');
    } catch (e) {
      toast.error(e.message || 'Fehler');
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20" data-testid="admin-companies-tab">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-white">Registrierte Firmen ({companies.length})</CardTitle>
            <CardDescription className="text-gray-400">
              A-Kunden bekommen Wunsch-Priorität und werden gehätschelt. C-Kunden können gezielt zum Laufzeitende gekündigt werden.
            </CardDescription>
          </div>
          <Button
            onClick={runCronTest}
            disabled={testingCron}
            variant="outline"
            className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10 shrink-0"
            data-testid="cron-test-btn"
          >
            {testingCron ? 'Sende…' : '📧 Ampel-Wochen-Mail jetzt testen'}
          </Button>
        </div>
        {cronResult && (
          <div className="mt-3 rounded-lg border border-yellow-500/30 bg-black/40 p-3 text-xs text-gray-300" data-testid="cron-test-result">
            <span className="text-emerald-400">✓ Gesendet: {cronResult.sent}</span>
            {' · '}
            <span className="text-amber-300">übersprungen: {cronResult.skipped}</span>
            {cronResult.errors && cronResult.errors.length > 0 && (
              <>
                {' · '}<span className="text-rose-300">Fehler: {cronResult.errors.length}</span>
                <details className="mt-1">
                  <summary className="cursor-pointer text-gray-500">Details</summary>
                  <ul className="mt-1 text-[10px] text-gray-400 space-y-0.5">
                    {cronResult.errors.slice(0, 5).map((e, i) => <li key={i}>· {e}</li>)}
                  </ul>
                </details>
              </>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {companies.map((company) => {
            const tier = tiers[company._id] || null;
            const meta = tier ? TIER_META[tier] : null;
            return (
              <div
                key={company._id}
                className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50"
                data-testid={`admin-company-${company._id}`}
              >
                <div className="flex flex-col md:flex-row justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-semibold">{company.name}</h3>
                      {meta && (
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.color}`}>
                          {meta.label}
                        </span>
                      )}
                      {termFlags[company._id] && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded-full px-2 py-0.5">
                          <AlertTriangle className="w-3 h-3" /> zur Kündigung markiert
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm truncate">{company.email}</p>
                    <div className="flex gap-4 mt-2 text-sm flex-wrap">
                      <span className="text-gray-500">
                        <Users className="h-4 w-4 inline mr-1" />
                        {company.employeeCount || 0} Mitarbeiter
                      </span>
                      <span className="text-gray-500">
                        <GraduationCap className="h-4 w-4 inline mr-1" />
                        {company.enrollmentCount || 0} Anmeldungen
                      </span>
                      <span className="text-gray-500">
                        Registriert: {company.createdAt ? new Date(company.createdAt).toLocaleDateString('de-DE') : ''}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <div className="flex rounded-lg overflow-hidden border border-gray-700" data-testid={`tier-picker-${company._id}`}>
                      {['A', 'B', 'C'].map((t) => (
                        <button
                          key={t}
                          onClick={() => setTier(company._id, t)}
                          className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                            tier === t ? TIER_META[t].color : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`}
                          data-testid={`tier-btn-${company._id}-${t}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {tier === 'A' && (
                      <button
                        onClick={() => inviteToBeta(company._id)}
                        className="text-[11px] text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 hover:bg-emerald-500/10 inline-flex items-center gap-1"
                        data-testid={`beta-invite-${company._id}`}
                        title="Kurs-Beta Einladung als E-Mail-Draft"
                      >
                        <Mail className="w-3 h-3" /> Zur Kurs-Beta einladen
                      </button>
                    )}
                    {tier === 'C' && !termFlags[company._id] && (
                      <button
                        onClick={() => markForTermination(company._id)}
                        className="text-[11px] text-rose-300 border border-rose-500/40 rounded-md px-2 py-1 hover:bg-rose-500/10"
                        data-testid={`terminate-btn-${company._id}`}
                      >
                        Zur Kündigung markieren
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {companies.length === 0 && (
            <p className="text-gray-400 text-center py-8">Keine Firmen registriert</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompaniesTab;
